import { useCallback } from 'react';
import type { FocusEventHandler, ReactNode } from 'react';

import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';

import { EMPTY_STRING } from '@e-socna/empty';

import type { IdOption } from '../IdAutocomplete';

// Native Select values are always strings in this codebase (Mongo-style ids),
// unlike `IdAutocomplete`'s TId which stays generic over React's full `Key`.
interface Props<TId extends string, TOption extends IdOption<TId>> {
  readonly autoFocus?: boolean;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly fullWidth?: boolean;
  readonly helperText?: ReactNode;
  readonly id: string;
  readonly label: string;
  readonly name?: string;
  readonly nullable?: boolean;
  readonly onBlur?: FocusEventHandler<HTMLElement>;
  readonly onChange: (id: TId | typeof EMPTY_STRING) => void;
  readonly options: readonly TOption[];
  readonly required?: boolean;
  readonly sx?: SxProps<Theme>;
  readonly value: TId | typeof EMPTY_STRING;
}

export const IdSelect = <TId extends string, TOption extends IdOption<TId>>({
  autoFocus = false,
  disabled = false,
  error = false,
  fullWidth = true,
  helperText,
  id,
  label,
  name,
  nullable = false,
  onBlur,
  onChange,
  options,
  required = false,
  sx,
  value,
}: Readonly<Props<TId, TOption>>) => {
  const handleChange = useCallback(
    (event: Readonly<SelectChangeEvent<TId | typeof EMPTY_STRING>>) => {
      onChange(event.target.value as TId | typeof EMPTY_STRING);
    },
    [onChange],
  );

  const labelId = `${id}-label`;

  return (
    <FormControl
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      required={required}
      sx={sx}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select<TId | typeof EMPTY_STRING>
        autoFocus={autoFocus}
        id={id}
        label={label}
        labelId={labelId}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        required={required}
        value={value}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!nullable && (
          <MenuItem value={EMPTY_STRING}>
            <em>Laisser vide</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
